import { useQuery } from "@tanstack/react-query";
import { ILookupAPIResp } from "../../../../core/_models";
import { useLookupData } from "../../../core/_queries";
import { QUERY_OPTIONS } from "../../../../../../utils/config";
import { getFeasibilityReadinessPercentage } from "./_request";

const defaultLookupResponse: ILookupAPIResp = { list: [], lookup: { display_name: "", dev_name: "" } };

const getQueryState = (query: ReturnType<typeof useLookupData>) => ({
  isLoading: query.isLoading,
  isError: query.isError,
  error: query.error,
});

export const useStakeHolderCommunicationReadinessData = () => {

  const query = useLookupData(
    "stakeholder-communication-readiness",
    ["investor_engagement_status", "government_coordination_level", "community_participation_status"]
  );

  return {
    investorEngagementStatus: query.data?.investor_engagement_status ?? defaultLookupResponse,
    communityEngagementStatus: query.data?.community_participation_status ?? defaultLookupResponse,
    governmentCoordinationLevel: query.data?.government_coordination_level ?? defaultLookupResponse,

    ...getQueryState(query)
  };
};

// export const useJustificationMasterDataQuery = () => {
export const useImplementationReadinessData = () => {

  const query = useLookupData(
    "implementation-readiness",
    ["key_constraints"]
  );

  return {
    keyConstraints: query.data?.key_constraints ?? defaultLookupResponse,
    
    ...getQueryState(query)
  };
};

export const useProjectStatusAndReadinessMasterDataQuery = () => {
    const query = useLookupData(
        "project-status-and-readiness-master-data",
        ["current_development_phase", "readiness_for_procurement", "regulatory_approvals_status", "stakeholder_engagement_status"]
    );

    return {
        currentDevelopmentPhases: query.data?.current_development_phase ?? defaultLookupResponse,
        readinessForProcurement: query.data?.readiness_for_procurement ?? defaultLookupResponse,
        regulatoryApprovalsStatus: query.data?.regulatory_approvals_status ?? defaultLookupResponse,
        stakeholderEngagementStatus: query.data?.stakeholder_engagement_status ?? defaultLookupResponse,

        ...getQueryState(query)
    };
};

export const feasibilityReadinessQueryKey = (projectId: number) => ["feasibility-readiness", projectId];

// SG3 readiness % and average risk score, shared by the header Overall Readiness card and the Stage
// Gate Decision Panel. Always refetched on mount (staleTime 0) and invalidated after a Stage 3 save.
export const useFeasibilityReadinessQuery = (projectId: number) => {
    const query = useQuery({
        queryKey: feasibilityReadinessQueryKey(projectId),
        queryFn: async ({ signal }) => {
            const response = await getFeasibilityReadinessPercentage({ project_id: projectId }, signal);

            if (response.status !== "ok" || !response.data) {
                throw new Error(response.message || "Failed to fetch feasibility readiness percentage");
            }

            return response.data;
        },
        enabled: !!projectId,
        ...QUERY_OPTIONS,
        staleTime: 0,
    });

    return {
        readiness: query.data ?? null,
        isFetching: query.isFetching,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
};
